
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  MapPin, Users, CheckCircle, Star, 
  ChevronDown, ChevronUp, Phone, Mail 
} from 'lucide-react';
import { JobVacancy } from '@/data/jobVacancies';

interface JobVacancyCardProps {
  vacancy: JobVacancy;
}

const JobVacancyCard: React.FC<JobVacancyCardProps> = ({ vacancy }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  const getLevelColor = (level: string) => {
    return level === 'senior' ? 'bg-brand-red/10 text-brand-red' : 'bg-brand-blue/10 text-brand-blue';
  };
  
  const getLanguageFlag = (language: string) => {
    return language === 'fr' ? '🇫🇷' : '🇳🇱';
  };
  
  const handleEmailClick = () => {
    window.location.href = `mailto:raisa@afsprakenmaker.be?subject=${vacancy.applicationEmail.subject}&body=${vacancy.applicationEmail.body}`;
  };

  const applyButtonText = vacancy.language === 'fr' ? 'Postulez maintenant' : 'Solliciteer nu';
  const detailsButtonText = vacancy.language === 'fr' ? 'Voir les détails' : 'Bekijk details';
  const hideDetailsText = vacancy.language === 'fr' ? 'Masquer les détails' : 'Verberg details';

  return (
    <Card className="h-full border-l-4 border-l-brand-blue hover:shadow-lg transition-shadow flex flex-col">
      {/* Header Section - Fixed Height */}
      <CardHeader className="flex-shrink-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex flex-wrap gap-2">
            <Badge className={getLevelColor(vacancy.level)}>
              {vacancy.level === 'senior' ? 'Senior' : 'Junior'}
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              <span>{getLanguageFlag(vacancy.language)}</span>
              {vacancy.language.toUpperCase()}
            </Badge>
            <Badge variant="secondary" className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {vacancy.location}
            </Badge>
          </div>
        </div>
        
        <CardTitle className="text-xl text-gray-900 mb-2">
          {vacancy.title}
        </CardTitle>
        
        <p className="text-gray-600">
          {vacancy.description}
        </p>
      </CardHeader>
      
      <CardContent className="flex flex-col flex-grow">
        {/* Preview Content Section - Fixed Height */}
        <div className="flex-shrink-0 mb-6">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Phone className="h-4 w-4 text-brand-blue" />
            {vacancy.language === 'fr' ? 'Responsabilités principales' : 'Hoofdtaken'}
          </h4>
          <ul className="space-y-2 min-h-[100px]">
            {vacancy.responsibilities.slice(0, 2).map((responsibility, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{responsibility}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Details Button Section - Fixed Position */}
        <div className="flex-shrink-0 border-t pt-4 mb-4">
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-0 h-auto font-normal">
                <span className="text-brand-blue">{isExpanded ? hideDetailsText : detailsButtonText}</span>
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="space-y-4 mt-4">
              {/* Phases (if applicable) */}
              {vacancy.phases && (
                <div className="space-y-3">
                  <div className="bg-brand-lightBlue/20 p-3 rounded-lg">
                    <h5 className="font-medium text-brand-blue mb-2">{vacancy.phases.phase1.title}</h5>
                    <ul className="space-y-1">
                      {vacancy.phases.phase1.tasks.map((task, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-3 w-3 text-green-500 mt-1 flex-shrink-0" />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-brand-red/10 p-3 rounded-lg">
                    <h5 className="font-medium text-brand-red mb-2">{vacancy.phases.phase2.title}</h5>
                    <ul className="space-y-1">
                      {vacancy.phases.phase2.tasks.map((task, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-3 w-3 text-green-500 mt-1 flex-shrink-0" />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* All Responsibilities */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {vacancy.language === 'fr' ? 'Toutes les responsabilités' : 'Alle verantwoordelijkheden'}
                </h4>
                <ul className="space-y-2">
                  {vacancy.responsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{responsibility}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-brand-red" />
                    {vacancy.language === 'fr' ? 'Indispensable' : 'Must-haves'}
                  </h4>
                  <ul className="space-y-2">
                    {vacancy.requirements.mustHaves.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {vacancy.requirements.niceToHaves.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      {vacancy.language === 'fr' ? 'Atouts' : 'Nice-to-haves'}
                    </h4>
                    <ul className="space-y-2">
                      {vacancy.requirements.niceToHaves.map((requirement, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <Star className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                          <span>{requirement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Work Location */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {vacancy.language === 'fr' ? 'Lieu de travail' : 'Werklocatie'}
                </h4>
                <p className="text-sm text-gray-600">{vacancy.workLocation}</p>
              </div>

              {/* Benefits */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {vacancy.language === 'fr' ? 'Ce que nous offrons' : 'Wat bieden wij'}
                </h4>
                <ul className="space-y-2">
                  {vacancy.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-brand-blue mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Apply Button Section - Fixed at bottom */}
        <div className="mt-auto">
          <Button 
            onClick={handleEmailClick}
            className="w-full bg-brand-red hover:bg-brand-red/90 text-white"
            size="lg"
          >
            <Mail className="h-4 w-4 mr-2" />
            {applyButtonText}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobVacancyCard;
